/**
 * GlobalState.conversations 切片与 Worker 数据源接线（任务 8c）。
 *
 * 写路径只有两条（UI 不直接改本切片，8d 只读渲染）：
 * - 快照：auth 变 logged-in（登录/冷启动/切账号，按 uid 去重）→ call
 *   'chat/conversations'——纯 IndexedDB 读零 HTTP，二次启动先渲染本地列表的落点。
 * - 推送：'chat/conversations'——upsert（WS 批内变更）按 key 合并重排；
 *   replace（connectok 对账后全量快照）整体替换。
 *
 * 顺序正确性（contract.ConversationsPush 约定）：快照响应与两种推送都产自
 * Worker 的 DB 写串行链，postMessage 到达序 = 链序 = 数据新旧序——按到达序
 * 应用即可，无需版本号。两个边界：
 * - rows 仍为 null（快照未回包）时丢弃 upsert——快照读在链上排在该变更之后，
 *   回包必然已包含它；
 * - 登出/换账号使在途快照过期（epoch 自增），旧响应不回写。
 */

import { compareConversations } from '~/chat/ordering';
import type { GlobalState, GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type { ConversationRecord, ConversationsPush } from '~/worker/contract';

export interface ConversationsSlice {
  /** null = 未加载（未登录/快照未回包）；[] = 已加载但无会话。始终按 compareConversations 排序 */
  readonly rows: readonly ConversationRecord[] | null;
}

export const CONVERSATIONS_INITIAL: ConversationsSlice = { rows: null };

/** 推送应用纯函数。null 态只接受 replace（文件头边界 1）；返回原引用表示无变化 */
export function applyConversationsPush(
  current: readonly ConversationRecord[] | null,
  push: ConversationsPush,
): readonly ConversationRecord[] | null {
  if (push.kind === 'replace') return push.rows;
  if (current === null || push.rows.length === 0) return current;
  const byKey = new Map(current.map((r) => [r.key, r] as const));
  for (const row of push.rows) byKey.set(row.key, row);
  return [...byKey.values()].sort((a, b) => compareConversations(a.remote, b.remote));
}

export interface ConversationsConnection {
  /** Worker 崩溃重建后重拉快照（死区内丢失的推送靠全量快照找平） */
  resync(): void;
  /**
   * 手动刷新（21ab，列表头「刷新」钮）：让 Worker 重新对账服务端第 1 页，
   * **再**重拉一次本地快照兜底。
   * 与 `resync()` 的区别：那个只重读本地快照（Worker 崩溃后找平用），
   * 这个会真的打一次 `content_list`——用户点刷新就是想要服务端的最新状态。
   */
  refresh(): Promise<void>;
  dispose(): void;
}

/** 首屏快照最多重试几次（首发之外）。退避 200/400/800/1600/3200ms */
export const SNAPSHOT_RETRY_MAX = 5;

/** 第 n 次失败后的退避毫秒（指数，封顶 4s） */
export function snapshotRetryDelay(attempt: number): number {
  return Math.min(4000, 200 * 2 ** attempt);
}

export function connectConversations(
  store: GlobalStore,
  host: AppWorkerHost,
): ConversationsConnection {
  let epoch = 0;
  let loadedUid: number | null = null;

  /**
   * 首屏快照加载。**失败要退避重试**（21ai）。
   *
   * 原先失败是静默吞掉的（`() => undefined`），而 `resync()` 只在 Worker 崩溃
   * 重建时才调——也就是说首次加载一旦失败，`rows` 就永远停在 null，首页渲染的是
   * `ConversationList` 那个 `height:100%` 的空 div：**一块永久空白，没有任何提示、
   * 也不会自己好**。人工报「从 web push 点进会话，返回首页是空白的」正是这条：
   * 通知唤起时应用刚起来，Worker 还在初始化，这一发很容易失败。
   *
   * 重试上限刻意给得宽（5 次、退避到 4s）：这是本地 RPC（读 IndexedDB），
   * 失败基本都是「Worker 还没就绪」这种瞬时原因，多等一会儿必然能成。
   */
  function load(attempt = 0): void {
    const seq = ++epoch;
    void host.call('chat/conversations', null).then(
      (snap) => {
        if (seq === epoch) store.set({ conversations: { rows: snap.rows } });
      },
      () => {
        // 期已过（登出/换账号/又发起了一次）就别再续了
        if (seq !== epoch || attempt >= SNAPSHOT_RETRY_MAX) return;
        setTimeout(() => {
          if (seq === epoch) load(attempt + 1);
        }, snapshotRetryDelay(attempt));
      },
    );
  }

  const offPush = host.onPush('chat/conversations', (push) => {
    store.set((prev) => {
      const rows = applyConversationsPush(prev.conversations.rows, push);
      return rows === prev.conversations.rows ? prev : { ...prev, conversations: { rows } };
    });
  });

  // auth 驱动：登录拉快照（uid 不变的重复 auth 同步不重拉）、登出清空。
  // 订阅不回放现值，接线时已 logged-in（理论不会，接线先于首个 auth 回包）补查一次。
  function onAuth(auth: GlobalState['auth']): void {
    if (auth.status === 'logged-in') {
      if (auth.userId !== loadedUid) {
        loadedUid = auth.userId;
        load();
      }
    } else if (auth.status === 'logged-out') {
      loadedUid = null;
      epoch += 1; // 在途快照过期（文件头边界 2）
      store.set({ conversations: CONVERSATIONS_INITIAL });
    }
  }
  const offAuth = store.subscribe((s) => s.auth, onAuth);
  onAuth(store.get().auth);

  return {
    resync() {
      if (loadedUid !== null) load();
    },
    async refresh() {
      if (loadedUid === null) return;
      await host.call('chat/conversations/resync', null).catch(() => undefined);
      // 对账结果本身会走 'chat/conversations' 推送回流；这里再拉一次本地快照，
      // 是为了「服务端没变化 → 没有推送」时按钮也有个确定的收尾
      load();
    },
    dispose() {
      offPush();
      offAuth();
    },
  };
}
